import { applyMiddleware, compose, createStore } from 'redux'
import thunk from 'redux-thunk'
import { browserHistory } from 'react-router'
import throttle from 'lodash/throttle'
import { isDevelopment, isStaging } from 'modules/app-config/environment'

// ========================================================
// BEGIN store setup additions
// ========================================================
import {
  hixmeNotifications,
  dispatchEvents,
  preserveRedirectSearch,
  restoreUrlAfterLogin,
} from 'middleware/middleware'
import { demoLoginEventMiddleware } from 'enrollment-platform/demo-monitoring/demo-login-event.middleware'
import { selectors as authSelectors } from '@hixme/auth-ui'
import userSession from 'store/modules/user-session'
import makeRootReducer from './reducers' // eslint-disable-line import/no-named-as-default
import { updateLocation } from './location'

// Load session data from local storage
const persistedState = userSession.utils.loadState()
// ========================================================
// END store setup additions
// ========================================================

export let storeInstance = null

/**
 * Persist a restorable location as userSession.currentUrl.
 * @param {object} options
 * @param {boolean} [options.requireAuth] only save while authenticated (nav listener)
 * @param {boolean} [options.flush] write localStorage immediately (boot / paste)
 */
const setCurrentUrlFromLocation = (
  store,
  location,
  { requireAuth = false, flush = false } = {}
) => {
  if (requireAuth && !authSelectors.isAuthenticated(store.getState())) {
    return
  }

  // Prefer browser search when react-router omits it (common when nav used `query`).
  const url = userSession.utils.locationToUrl({
    pathname: location.pathname,
    search: location.search || window.location.search,
    query: location.query,
  })

  // Skip `/` so LoginContainer's post-login push (and boot on `/`) cannot
  // wipe the last real page before restoreCurrentUrl runs.
  if (!userSession.utils.isPersistableUrl(url)) {
    return
  }

  if (userSession.selectors.getCurrentUrl(store.getState()) === url) {
    return
  }

  store.dispatch(userSession.actions.setCurrentUrl(url))

  if (flush) {
    userSession.utils.saveState({
      userSession: {
        ...store.getState().userSession,
        currentUrl: url,
      },
    })
  }
}

export default (initialState = {}, extras = {}) => {
  // ======================================================
  // Middleware Configuration
  // ======================================================

  // SureCo change - adding of hixmeNotifications
  const middleware = [
    thunk.withExtraArgument(extras),
    restoreUrlAfterLogin,
    demoLoginEventMiddleware,
    hixmeNotifications,
    dispatchEvents(extras.emitter),
    preserveRedirectSearch,
  ]

  // ======================================================
  // Store Enhancers
  // ======================================================
  const enhancers = []
  if (isDevelopment() || isStaging()) {
    // eslint-disable-next-line no-underscore-dangle
    const devToolsExtension = window.__REDUX_DEVTOOLS_EXTENSION__
    if (typeof devToolsExtension === 'function') {
      enhancers.push(devToolsExtension())
    }
  }

  // ======================================================
  // Store Instantiation and HMR Setup
  // ======================================================
  const store = createStore(
    makeRootReducer(),
    // SureCo change - adding of persistedState for session handling
    Object.assign({}, initialState, persistedState),
    compose(applyMiddleware(...middleware), ...enhancers)
  )
  store.asyncReducers = {}

  // Paste/direct entry: replace any previously persisted currentUrl immediately.
  setCurrentUrlFromLocation(
    store,
    {
      pathname: window.location.pathname,
      search: window.location.search,
    },
    { flush: true }
  )

  // To unsubscribe, invoke `store.unsubscribeHistory()` anytime
  store.unsubscribeHistory = browserHistory.listen((location) => {
    updateLocation(store)(location)
    setCurrentUrlFromLocation(store, location, { requireAuth: true })
  })

  // ========================================================
  // BEGIN SureCo store subscribe
  // ========================================================
  store.subscribe(
    throttle(() => {
      userSession.utils.saveState({
        userSession: store.getState().userSession,
      })
    }, 1000)
  )
  // ========================================================
  // END SureCo store subscribe
  // ========================================================

  if (module.hot) {
    module.hot.accept('./reducers', () => {
      const reducers = require('./reducers').default // eslint-disable-line global-require
      store.replaceReducer(reducers(store.asyncReducers))
    })
  }

  storeInstance = store
  return store
}
